import CommonTitleBar from "components/common/CommonTitleBar";
import Filters from "components/common/Filters";
import NoDataFound from "components/common/NoDataFound";
import Paginations from "components/common/Paginations";
import Spinners from "components/common/Spinners";
import {
  formatDate,
  formatDateTime,
  formatToCustomDateTime,
  getKeywordValue,
  getMinMaxDate,
} from "components/common/Utils";
import {
  apiLogsResponseOptions,
  apiLogsStatusOptions,
  apiUrls,
  orderByOptions,
  statusOptions,
} from "components/constants/StatusKeys";
import TableShimmer from "components/ShimmerEffects/CommonShimmer/TableShimmer";
import React, { useEffect, useState } from "react";
import { Badge, Col, Container, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getAPILogs } from "store/actions/globalSettingSlice";
import {
  resetApiLogsFilterState,
  updateApiLogsFilterState,
} from "store/reducers/globalSettingReducer";

const APILogs = () => {
  const { t } = useTranslation();
  document.title = t("API Logs | Da Nonna");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { search } = useLocation();
  const pageNumber = Number(new URLSearchParams(search).get("page")) || 1;

  const {
    isLoading,
    loading,
    perPage,
    totalCount,
    totalPages,
    currentPage,
    apiLogs,
    apiLogsFilterState,
  } = useSelector((state) => state.GlobalSettings);

  const { keyword, dates, selectedFilters } = apiLogsFilterState;

  // Update setters to dispatch actions:
  const setKeyword = (value) =>
    dispatch(updateApiLogsFilterState({ keyword: value }));
  const setDates = (value) =>
    dispatch(updateApiLogsFilterState({ dates: value }));
  const setSelectedFilters = (value) => {
    const updatedFilters =
      typeof value === "function" ? value(selectedFilters) : value;
    dispatch(updateApiLogsFilterState({ selectedFilters: updatedFilters }));
  };

  // const [keyword, setKeyword] = useState("");
  // const [dates, setDates] = useState([]);

  // const [selectedFilters, setSelectedFilters] = useState({
  //   status: -1,
  //   orderBy: -1,
  //   responseStatus: 0,
  //   url: "",
  // });

  useEffect(() => {
    getAllData();
  }, []);

  const getAllData = (page, action) => {
    const { actionType } = action || {};
    const { status, orderBy, responseStatus, url } = selectedFilters;
    const isReset = actionType === "RESET";

    const payload = {
      perPage,
      pageNo: page || apiLogsFilterState.pageNumber,
      status: isReset ? -1 : status,
      orderBy: isReset ? -1 : orderBy,
      dateRange: isReset ? {} : getMinMaxDate(dates),
      keyword: getKeywordValue(actionType, keyword),
      responseStatus: isReset ? 0 : responseStatus,
      url: isReset ? "" : url,
    };

    dispatch(getAPILogs(payload));
  };

  const filters = [
    {
      key: "status",
      placeholder: "Status",
      options: apiLogsStatusOptions,
      type: "singleSelect",
    },
    {
      key: "url",
      placeholder: "URL",
      options: apiUrls,
      type: "singleSelect",
    },
    {
      key: "responseStatus",
      placeholder: "Response Status",
      options: apiLogsResponseOptions,
      type: "singleSelect",
    },
    {
      key: "orderBy",
      placeholder: "Order By",
      options: orderByOptions,
      type: "singleSelect",
    },
  ];

  const handleReset = () => {
    dispatch(resetApiLogsFilterState());
    getAllData(1, { actionType: "RESET" });
  };

  return (
    <React.Fragment>
      <div className="page-content common_page">
        <Container fluid>
          <CommonTitleBar title={t("API Logs")} />
          <div className="common_wrapper">
            <Filters
              keyword={keyword}
              setKeyword={setKeyword}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              dates={dates}
              setDates={setDates}
              filters={filters}
              onSearch={getAllData}
              onReset={handleReset}
            />
            <Row>
              <Col lg={12}>
                {loading ? (
                  <TableShimmer col={9} />
                ) : apiLogs.length === 0 ? (
                  <NoDataFound />
                ) : (
                  <div className="common_table_style">
                    <Table>
                      <thead className="t_head">
                        <tr>
                          <th>#</th>
                          <th>{t("Customer Name")}</th>
                          <th>{t("Phone Number")}</th>
                          <th>{t("Method")}</th>
                          <th>{t("Path")}</th>
                          <th>{t("Status Code")}</th>
                          <th>{t("Result")}</th>
                          <th>{t("Duration")}</th>
                          <th>{t("Date")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {apiLogs?.map((item, index) => (
                          <tr
                            key={item?._id}
                            className="c_p"
                            onClick={() =>
                              navigate(
                                `/management/api-logs/details?id=${item?._id}`
                              )
                            }
                          >
                            <td>{(currentPage - 1) * perPage + index + 1}</td>
                            <td>{item?.userInfo?.name || "-"}</td>
                            <td>{item?.userInfo?.phone || "-"}</td>
                            <td>
                              <p className="fs_12 bg-primary w_fit_content px-2 text-white rounded fw-bold">
                                {item?.data?.method || "-"}
                              </p>
                            </td>
                            <td>
                              <p style={{ maxWidth: 300, overflow: "auto" }}>
                                {item?.data?.url || "-"}
                              </p>
                            </td>
                            <td>
                              <span
                                className={`fs_14 ${
                                  item?.data?.responseStatus === 200
                                    ? "bg-success"
                                    : [400, 500]?.includes(
                                        item?.data?.responseStatus
                                      )
                                    ? "bg-danger"
                                    : "bg-warning"
                                }
                                 text-white fw-bold px-1 rounded`}
                              >
                                <small>{item?.data?.responseStatus}</small>
                              </span>
                            </td>
                            <td>
                              {item?.data?.responseStatus &&
                              item?.data?.status === 1 ? (
                                <Badge className="bg-success">
                                  {t("Success")}
                                </Badge>
                              ) : (
                                <Badge className="bg-danger">
                                  {t("Failed")}
                                </Badge>
                              )}
                            </td>
                            <td>{item?.data?.duration || "-"}</td>
                            <td>
                              <p className="w_fit_content">
                                {formatToCustomDateTime(item?.createdAt)}
                              </p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Col>
            </Row>
          </div>
          {!loading && (
            <Paginations
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                getAllData(page);
              }}
              totalCount={totalCount}
            />
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default APILogs;
